import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Subject } from 'rxjs';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { NeedAttributes } from 'src/app/controls/workflows/need-attributes';
import { NeedCustomers } from 'src/app/controls/workflows/need-customers';
import { NeedEmailTemplates } from 'src/app/controls/workflows/need-email-templates';
import { NeedFolderStates } from 'src/app/controls/workflows/need-folder-states';
import { NeedSteps } from 'src/app/controls/workflows/need-steps';
import { NeedUsers } from 'src/app/controls/workflows/need-users';
import { NeedWorkflow } from 'src/app/controls/workflows/need-workflow';
import { WorkflowDataInterface } from 'src/app/controls/workflows/workflow-data.interface';
import { WorkflowNotImplementedTriggerDataComponent } from 'src/app/controls/workflows/workflow-not-implemented-trigger-data/workflow-not-implemented-trigger-data.component';
import { WorkflowTriggerAttributeChangedDataComponent } from 'src/app/controls/workflows/workflow-trigger-attribute-changed-data/workflow-trigger-attribute-changed-data.component';
import { WorkflowTriggerDateReachedDataComponent } from 'src/app/controls/workflows/workflow-trigger-date-reached-data/workflow-trigger-date-reached-data.component';
import { WorkflowTriggerExpiredDelayDataComponent } from 'src/app/controls/workflows/workflow-trigger-expired-delay-data/workflow-trigger-expired-delay-data.component';
import { WorkflowTriggerFolderCreatedComponent } from 'src/app/controls/workflows/workflow-trigger-folder-created/workflow-trigger-folder-created';
import { WokflowTriggerStepActivatedDataComponent } from 'src/app/controls/workflows/workflow-trigger-step-activated-data/workflow-trigger-step-activated-data.component';
import { WorkflowTriggerStepCompletedDataComponent } from 'src/app/controls/workflows/workflow-trigger-step-completed-data/workflow-trigger-step-completed-data.component';
import { WokflowTriggerStepStateChangedDataComponent } from 'src/app/controls/workflows/workflow-trigger-step-state-changed-data/workflow-trigger-step-state-changed-data.component';
import { WorkflowTriggerTimerDataComponent } from 'src/app/controls/workflows/workflow-trigger-timer-data/workflow-trigger-timer-data.component';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { CustomersService } from 'src/app/services/customers.service';
import { EmailsTemplatesService } from 'src/app/services/emails-templates.service';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { MemoryService } from 'src/app/services/memory.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkflowDefinitionsService } from 'src/app/services/workflows/workflow-definitions.service';
import { AnyTriggerData } from '../../../models/Types/any-trigger-data';
import { Workflow } from '../../../models/workflow';
import { IFormComponent } from '../core/form-component.interface';

/**
 * Composants affichant le formulaire d'édition du trigger de workflow selectionnée dans le designer de workflow.
 */
@Component({
    selector: 'app-workflow-trigger-form',
    templateUrl: './workflow-trigger-form.html',
    styleUrl: './workflow-trigger-form.scss',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, CommonModule],
})
export class WorkflowTriggerFormComponent implements IFormComponent, OnInit, AfterViewInit {
    /**
     * Reference au container dans lequel est affiché les composants dynamiques.
     */
    @ViewChild('dynamicContainer', { read: ViewContainerRef })
    dynamicContainer!: ViewContainerRef;

    /**
     * Reference au composant dynamique affichant les données de l'action.
     */
    componentRef: ComponentRef<WorkflowDataInterface>;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected dynamicComponentDestroyed$: Subject<void> = new Subject<void>();
    /**
     *  Données du trigger affichées dans le formulaire.
     */
    public _data: AnyTriggerData;

    /**
     * Spécifie les données du trigger affichées dans le formulaire.
     */
    set data(value: AnyTriggerData) {
        this._data = value;
        this.fillForm();
    }

    /**
     * Obtient les données du trigger affichées dans le formulaire.
     */
    get data(): AnyTriggerData {
        this.fillData();
        return this._data;
    }

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    get isDirty(): boolean {
        return this.dataForm.dirty || (this.componentRef && this.componentRef.instance.dirty);
    }

    /**
     * Instance du workflow.
     */
    public workflow: Workflow;

    /**
     * Liste des différents types de triggers affichés dans le menu déroulant.
     */
    public triggerTypes: { value: WorkflowTriggerTypes; name: string }[] = [
        { value: WorkflowTriggerTypes.AttributeChanged, name: 'Un attribut a changé de valeur' },
        { value: WorkflowTriggerTypes.DateReached, name: 'Une date a été atteinte' },
        { value: WorkflowTriggerTypes.DelayExpired, name: 'Un délai est échu' },
        { value: WorkflowTriggerTypes.EmailSent, name: 'Un email a été envoyé' },
        { value: WorkflowTriggerTypes.FolderCreated, name: 'Le dossier a été créé' },
        { value: WorkflowTriggerTypes.MailReceived, name: 'Un email a été reçu' },
        { value: WorkflowTriggerTypes.SmsReceived, name: 'Un SMS a été reçu' },
        { value: WorkflowTriggerTypes.SmsSent, name: 'Un SMS a été envoyé' },
        { value: WorkflowTriggerTypes.StepActivated, name: "L'étape a été activée" },
        { value: WorkflowTriggerTypes.StepCompleted, name: "L'étape est cloturée" },
        { value: WorkflowTriggerTypes.StepStateChanged, name: "L'état de l'étape a été modifié" },
        { value: WorkflowTriggerTypes.Timer, name: 'Minuteur' },
    ];

    /**
     * Constructeur.
     * @param changeDetector : Detecteur de changement.
     */
    constructor(
        private changeDetector: ChangeDetectorRef,
        private emailsTemplatesService: EmailsTemplatesService,
        private memoryService: MemoryService,
        private attributesService: AttributeDefinitionsService,
        private foldersStatesService: FoldersStatesService,
        private usersService: UsersService,
        protected customersService: CustomersService,
        private workflowDefinitionsService: WorkflowDefinitionsService,
        private spinner: NgxSpinnerService
    ) {
        console.log('WorkflowTriggerFormComponent - constructor : ');
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.dataForm = new UntypedFormGroup({
            id: new UntypedFormControl({
                value: '',
                disabled: true,
            }),
            type: new UntypedFormControl({ value: '', disabled: true }),
        });

        this.fillForm();
    }

    ngAfterViewInit(): void {
        this.fillForm();
    }

    /**
     * Remplit le formulaire avec les données du trigger.
     */
    private fillForm() {
        if (this._data && this.dynamicContainer) {
            if (this.dataForm) {
                this.dataForm.get('id').setValue(this._data.id);
                this.dataForm.get('type').setValue(this._data.type);
            }

            this.loadDataComponent(this._data.type);
        }
    }

    /**
     * Récupère les données à partir des champs du formulaire et met à jour l'object data.
     */
    public fillData() {
        let triggerData: AnyTriggerData = this.componentRef.instance.data as AnyTriggerData;

        triggerData.id = this.dataForm.get('id').value;

        this._data = triggerData;

        this.clearData();

        console.log('Fill data', this._data);
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.componentRef.instance.clearData();
        this.dataForm.markAsPristine();
    }

    /**
     * Charge dynamique le composant de saisie des données de l'action.
     * @param type : Type de l'action.
     */
    private loadDataComponent(type: WorkflowTriggerTypes) {
        this.dynamicContainer.clear();
        this.dynamicComponentDestroyed$.next();

        if (type) {
            switch (type) {
                case WorkflowTriggerTypes.DelayExpired:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowTriggerExpiredDelayDataComponent);

                    console.log('WorkflowTriggerFormComponent - loadDataComponent --> WorkflowTriggerDelayExpiredDataComponent');
                    break;
                case WorkflowTriggerTypes.Timer:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowTriggerTimerDataComponent);

                    console.log('WorkflowTriggerFormComponent - loadDataComponent --> WorkflowTriggerTimerDataComponent');
                    break;
                case WorkflowTriggerTypes.StepStateChanged:
                    this.componentRef = this.dynamicContainer.createComponent(WokflowTriggerStepStateChangedDataComponent);
                    console.log('WorkflowTriggerFormComponent - loadDataComponent');
                    break;
                case WorkflowTriggerTypes.StepActivated:
                    this.componentRef = this.dynamicContainer.createComponent(WokflowTriggerStepActivatedDataComponent);

                    break;
                case WorkflowTriggerTypes.StepCompleted:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowTriggerStepCompletedDataComponent);

                    break;
                case WorkflowTriggerTypes.AttributeChanged:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowTriggerAttributeChangedDataComponent);
                    console.log('WorkflowTriggerFormComponent - loadDataComponent --> WorkflowTriggerAttributeChangedDataComponent');
                    break;
                case WorkflowTriggerTypes.DateReached:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowTriggerDateReachedDataComponent);
                    console.log('WorkflowTriggerFormComponent - loadDataComponent --> WorkflowTriggerDateReachedDataComponent');
                    break;
                case WorkflowTriggerTypes.FolderCreated:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowTriggerFolderCreatedComponent);
                    console.log('WorkflowTriggerFormComponent - loadDataComponent --> WorkflowTriggerFolderCreatedComponent');
                    break;
                default:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowNotImplementedTriggerDataComponent);
                    console.log('WorkflowTriggerFormComponent - loadDataComponent --> WorkflowNotImplementedTriggerDataComponent');
                    break;
            }

            if (this._data) {
                this.componentRef.instance.data = this._data;
            }

            if (this.IsComponentNeedAttributes()) {
                this.loadAttributes(this.componentRef.instance as unknown as NeedAttributes);
            }

            if (this.IsComponentNeedEmailTemplates()) {
                this.loadEmailTemplates(this.componentRef.instance as unknown as NeedEmailTemplates);
            }

            if (this.IsComponentNeedFolderStates()) {
                this.loadFolderStates(this.componentRef.instance as unknown as NeedFolderStates);
            }

            if (this.IsComponentNeedCustomers()) {
                this.loadCustomers(this.componentRef.instance as unknown as NeedCustomers);
            }

            if (this.IsComponentNeedSteps()) {
                this.loadSteps(this.componentRef.instance as unknown as NeedSteps);
            }

            if (this.IsComponentNeedWorkflow()) {
                (this.componentRef.instance as unknown as NeedWorkflow).workflow = this.workflow;
            }

            if (this.IsComponentNeedWorkflow()) {
                (this.componentRef.instance as unknown as NeedWorkflow).workflow = this.workflow;
            }

            if (this.IsComponentNeedUsers()) {
                this.loadUsers(this.componentRef.instance as unknown as NeedUsers);
            }
        }
    }

    /**
     * Charge les étape de workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadSteps(componentInstance: NeedSteps) {
        componentInstance.steps = this.workflow.steps;
    }

    /**
     * Charge les attributs du workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadAttributes(componentInstance: NeedAttributes) {
        this.spinner.show('detail-bar-spinner');
        this.attributesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let attributes = x.value.sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });
            componentInstance.attributes = attributes;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les états de dossier et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadFolderStates(componentInstance: NeedFolderStates) {
        this.foldersStatesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let folderStates = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.folderStates = folderStates;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les utilisateurs de dossier et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadUsers(componentInstance: NeedUsers) {
        this.usersService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let users = x.value.sort((a, b) => {
                return a.displayName < b.displayName ? -1 : 1;
            });
            componentInstance.users = users;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les clients et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadCustomers(componentInstance: NeedCustomers) {
        this.spinner.show('main-spinner');
        this.customersService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let customers = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.customers = customers;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les modèles de mails et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadEmailTemplates(componentInstance: NeedEmailTemplates) {
        this.spinner.show('detail-bar-spinner');
        const viewContext: Context = {
            property: 'viewId',
            value: this.memoryService.getSelectedView().id,
        };
        const getOrganizationTemplates$ = this.emailsTemplatesService.List(null, null, null, null, 0, 1000);
        const getViewTemplates$ = this.emailsTemplatesService.List(viewContext, null, null, null, 0, 1000);

        forkJoin([getOrganizationTemplates$, getViewTemplates$]).subscribe((x) => {
            let templates = x[0].value.concat(x[1].value);
            templates = templates.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.templates = templates;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des états de dossier.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedFolderStates() {
        return 'folderStates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des clients.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedCustomers() {
        // Vérifie que le composant possède la propriété 'folderStates'.
        return 'customers' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des attributs.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedAttributes() {
        return 'attributes' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des modèles de mail.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedEmailTemplates() {
        return 'templates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de l'instance de workflow.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedWorkflow() {
        return 'workflow' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des étapes.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedSteps() {
        return 'steps' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des utilisateurs.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedUsers() {
        return 'users' in this.componentRef.instance;
    }
}
